import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { SupplierAudit } from '../../lib/api/audits';
import { Card, CardContent } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import SearchableSelect from '../../components/ui/SearchableSelect';
import { ListPager, ListSearch, useListControls } from '../../components/common/listControls';
import { SortableTh } from '../../components/common/SortableTh';
import { UploadTableEmpty, UploadTableSkeleton } from '../barratt/upload-history/UploadTableStates';
import { formatDay } from '../barratt/uploadCycle';
import { Download } from 'lucide-react';
import * as XLSX from 'xlsx';
import { AUDIT_TYPE_LABELS, AuditOutcomeBadge, AuditStatusBadge } from './auditStatus';

type StatusFilter = 'open' | 'completed' | 'cancelled' | 'all';

interface AuditsTableProps {
  audits: SupplierAudit[];
  loading: boolean;
  /** Suppliers cannot plan or edit; the CTA in the empty state is hidden. */
  readOnly: boolean;
  onPlan?: () => void;
}

const COLUMNS = 8;

/** The Audits list — in-memory search, sort and paging over every audit in scope. */
export default function AuditsTable({ audits, loading, readOnly, onPlan }: AuditsTableProps) {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('open');
  const [typeFilter, setTypeFilter] = useState('');

  const filtered = useMemo(() => audits.filter((a) => {
    if (statusFilter === 'open' && !(a.status === 'planned' || a.status === 'in_progress')) return false;
    if (statusFilter === 'completed' && a.status !== 'completed') return false;
    if (statusFilter === 'cancelled' && a.status !== 'cancelled') return false;
    if (typeFilter && a.audit_type !== typeFilter) return false;
    return true;
  }), [audits, statusFilter, typeFilter]);

  const list = useListControls(filtered, {
    searchText: (a) => [a.reference, a.title, a.supplier_name, a.division_name ?? '', a.auditor_name].join(' '),
    sorters: {
      scheduled_date: (a) => a.scheduled_date,
      reference: (a) => a.reference,
      supplier: (a) => a.supplier_name.toLowerCase(),
      type: (a) => a.audit_type_display,
      status: (a) => a.status,
      actions: (a) => a.open_actions,
    },
    defaultSort: { key: 'scheduled_date', dir: 'desc' },
    tieBreak: (a) => a.reference,
    resetKeys: [statusFilter, typeFilter],
  });

  const counts = useMemo(() => ({
    open: audits.filter((a) => a.status === 'planned' || a.status === 'in_progress').length,
    completed: audits.filter((a) => a.status === 'completed').length,
    cancelled: audits.filter((a) => a.status === 'cancelled').length,
    all: audits.length,
  }), [audits]);

  const exportExcel = () => {
    const rows = list.orderedRows.map((a) => [
      a.reference, a.title, a.supplier_name, a.division_name ?? '', a.audit_type_display, a.scheduled_date,
      a.auditor_name, a.status_display, a.outcome_display, a.score ?? '', a.open_actions, a.total_actions,
    ]);
    const sheet = XLSX.utils.aoa_to_sheet([
      ['Reference', 'Title', 'Supplier', 'Division', 'Type', 'Scheduled', 'Auditor', 'Status', 'Outcome', 'Score', 'Open actions', 'Actions'],
      ...rows,
    ]);
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, 'Audits');
    const parts = ['audits', statusFilter !== 'all' ? statusFilter : '', typeFilter].filter(Boolean);
    XLSX.writeFile(book, `${parts.join('-')}.xlsx`);
  };

  const typeOptions = Object.entries(AUDIT_TYPE_LABELS).map(([value, label]) => ({ value, label }));
  const emptyMessage = audits.length === 0
    ? (readOnly ? 'No audits have been raised on your company.' : 'No audits planned yet — plan the first one.')
    : list.search
      ? `No audits match "${list.search}".`
      : 'No audits in this view.';

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <Tabs value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
          <TabsList data-testid="audits-status-filter">
            <TabsTrigger value="open">Open ({counts.open})</TabsTrigger>
            <TabsTrigger value="completed">Completed ({counts.completed})</TabsTrigger>
            <TabsTrigger value="cancelled">Cancelled ({counts.cancelled})</TabsTrigger>
            <TabsTrigger value="all">All ({counts.all})</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex flex-col gap-2 md:flex-row md:items-center">
          <div className="md:w-72">
            <ListSearch value={list.search} onChange={list.setSearch} placeholder="Search reference, title, supplier, auditor…" testId="audits-search" />
          </div>
          <div className="md:w-56">
            <SearchableSelect options={typeOptions} value={typeFilter} onChange={setTypeFilter} placeholder="All types" />
          </div>
          <Button variant="outline" size="sm" onClick={exportExcel} disabled={list.orderedRows.length === 0} data-testid="audits-export">
            <Download className="mr-2 h-4 w-4" />Export
          </Button>
          {!readOnly && onPlan && (
            <Button size="sm" onClick={onPlan} data-testid="audits-plan">Plan audit</Button>
          )}
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <ListPager list={list} position="top" noun="audits" testIdPrefix="audits" />
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTh field="reference" sortField={list.sortKey} sortDirection={list.sortDir} onSort={list.sortBy}>Reference</SortableTh>
                  <TableHead>Title</TableHead>
                  <SortableTh field="supplier" sortField={list.sortKey} sortDirection={list.sortDir} onSort={list.sortBy}>Supplier</SortableTh>
                  <SortableTh field="type" sortField={list.sortKey} sortDirection={list.sortDir} onSort={list.sortBy}>Type</SortableTh>
                  <SortableTh field="scheduled_date" sortField={list.sortKey} sortDirection={list.sortDir} onSort={list.sortBy}>Scheduled</SortableTh>
                  <SortableTh field="status" sortField={list.sortKey} sortDirection={list.sortDir} onSort={list.sortBy}>Status</SortableTh>
                  <TableHead>Outcome</TableHead>
                  <SortableTh field="actions" sortField={list.sortKey} sortDirection={list.sortDir} onSort={list.sortBy} align="right">Open actions</SortableTh>
                </TableRow>
              </TableHeader>
              {loading ? (
                <UploadTableSkeleton columns={COLUMNS} />
              ) : list.pageRows.length === 0 ? (
                <UploadTableEmpty columns={COLUMNS} message={emptyMessage} />
              ) : (
                <TableBody>
                  {list.pageRows.map((a) => (
                    <TableRow key={a.id} className={`cursor-pointer ${a.is_overdue ? 'border-l-4 border-l-red-500' : ''}`}
                              onClick={() => navigate(`/audits/${a.id}`)} data-testid={`audit-${a.id}`}>
                      <TableCell className="font-medium">{a.reference}</TableCell>
                      <TableCell>
                        <div className="text-foreground">{a.title}</div>
                        {a.division_name && <div className="text-xs text-muted-foreground">{a.division_name}</div>}
                      </TableCell>
                      <TableCell>{a.supplier_name}</TableCell>
                      <TableCell>{a.audit_type_display}</TableCell>
                      <TableCell>
                        <div>{formatDay(a.scheduled_date)}</div>
                        <div className="text-xs text-muted-foreground">{a.auditor_name}</div>
                      </TableCell>
                      <TableCell><AuditStatusBadge status={a.status} overdue={a.is_overdue} /></TableCell>
                      <TableCell><AuditOutcomeBadge outcome={a.outcome} /></TableCell>
                      <TableCell className="text-right">
                        <span className="font-semibold">{a.open_actions}</span>
                        <span className="text-muted-foreground"> / {a.total_actions}</span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>
          </div>
          <ListPager list={list} position="bottom" noun="audits" testIdPrefix="audits" />
        </CardContent>
      </Card>
    </div>
  );
}
